import React, { useState, useEffect, useCallback, useRef } from "react";
import DataGrid, {
  Column, FilterRow, HeaderFilter, FilterPanel,
  Scrolling, LoadPanel, SearchPanel, ColumnChooser,
  ColumnChooserSearch, ColumnChooserSelection, Position,
  Paging, Pager, Export, StateStoring,
} from "devextreme-react/data-grid";
import Button1 from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Container from "@material-ui/core/Container";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { exportDataGrid } from "devextreme/excel_exporter";
import { useStyles } from "../../Styles.js";
import useDeviceSize from "../../hooks/useDeviceSize.js";
import { api_path } from "../../shared.js";

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const LOG_API         = `${api_path}/tracking/rpa-log.php`;
const AUTO_REFRESH_MS = 10 * 60 * 1000; // 5 นาที
const PAGE_SIZES      = [25, 50, 100];
const SEARCH_OPTIONS  = { placeholder: "Search column" };

const today = new Date().toISOString().slice(0, 10);

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ toast }) => {
  if (!toast) return null;
  return (
    <div style={{
      position: "fixed", top: 16, right: 16, zIndex: 9999,
      background: toast.type === "error" ? "#fef2f2" : "#f0fdf4",
      color:      toast.type === "error" ? "#dc2626" : "#15803d",
      border:     `1px solid ${toast.type === "error" ? "#fca5a5" : "#86efac"}`,
      borderRadius: 10, padding: "10px 20px",
      fontWeight: 700, fontSize: 13, boxShadow: "0 4px 16px #0002",
    }}>{toast.msg}</div>
  );
};

// ─── Summary Cards ────────────────────────────────────────────────────────────
const CARDS = [
  { key: "total",         label: "ทั้งหมด",      icon: "📦", color: "#1a237e" },
  { key: "success_count", label: "สำเร็จ",       icon: "✅", color: "#16a34a" },
  { key: "failed_count",  label: "ล้มเหลว",      icon: "❌", color: "#dc2626" },
  { key: "success_rate",  label: "Success Rate", icon: "📊", color: "#d97706", suffix: "%" },
  { key: "avg_ms",        label: "Avg (ms)",     icon: "⚡", color: "#7c3aed" },
];

const SummaryCards = ({ summary }) => (
  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", margin: "10px 0 16px" }}>
    {CARDS.map(({ key, label, icon, color, suffix }) => {
      const raw   = summary?.[key];
      const value = raw != null ? `${raw}${suffix ?? ""}` : "—";
      const isFail = key === "failed_count" && Number(raw) > 0;
      const cardColor = isFail ? "#dc2626" : color;
      return (
        <div key={key} style={{
          flex: "1 1 140px", minWidth: 130,
          background: "#fff",
          border: `1px solid #e2e8f0`,
          borderTop: `4px solid ${cardColor}`,
          borderRadius: 8, padding: "14px 16px",
          boxShadow: "0 1px 4px #0001",
        }}>
          <div style={{ fontSize: 22, marginBottom: 4 }}>{icon}</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: cardColor, lineHeight: 1 }}>{value}</div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>{label}</div>
        </div>
      );
    })}
  </div>
);

// ─── Status Badge (cell render) ───────────────────────────────────────────────
const StatusBadge = ({ data }) => {
  const isSuccess = data?.status === "success";
  return (
    <span style={{
      background: isSuccess ? "#f0fdf4" : "#fef2f2",
      color:      isSuccess ? "#16a34a" : "#dc2626",
      border:     `1px solid ${isSuccess ? "#86efac" : "#fca5a5"}`,
      borderRadius: 4, padding: "2px 8px",
      fontSize: 11, fontWeight: 800, whiteSpace: "nowrap",
    }}>
      {isSuccess ? "✅ Success" : "❌ Failed"}
    </span>
  );
};

// ─── Error Cell (truncated with title) ───────────────────────────────────────
const ErrorCell = ({ data }) => {
  if (!data?.error_message) return null;
  return (
    <span title={data.error_message} style={{
      color: "#dc2626", fontSize: 12,
      display: "block", maxWidth: 320,
      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    }}>
      {data.error_message}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function RpaMonitor() {
  const [, height] = useDeviceSize();
  const classes    = useStyles();
  const gridRef    = useRef(null);

  // ─── State ──────────────────────────────────────────────────────────────────
  const [dateFrom,     setDateFrom]     = useState(today);
  const [dateTo,       setDateTo]       = useState(today);
  const [statusFilter, setStatusFilter] = useState("");

  const [summary,     setSummary]     = useState(null);
  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [toast,       setToast]       = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  // ─── Toast helper ────────────────────────────────────────────────────────────
  const showToast = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // ─── Fetch Summary ───────────────────────────────────────────────────────────
  const fetchSummary = useCallback(async (from, to) => {
    try {
      const p   = new URLSearchParams({ api: "summary", date_from: from, date_to: to });
      const res = await fetch(`${LOG_API}?${p}`, { credentials: "include" });
      const json = await res.json();
      if (json.success) setSummary(json.data);
    } catch (e) {
      console.error("fetchSummary:", e);
    }
  }, []);

  // ─── Fetch List (all, DataGrid handles paging/filter client-side) ─────────────
  const fetchList = useCallback(async (from, to, status) => {
    setLoading(true);
    try {
      const p = new URLSearchParams({
        api: "list", date_from: from, date_to: to,
        status: status ?? "", page: 1, page_size: 500,
      });
      const res  = await fetch(`${LOG_API}?${p}`, { credentials: "include" });
      const json = await res.json();
      if (json.success) {
        setRows(json.data ?? []);
        setLastRefresh(new Date());
      } else {
        showToast("❌ " + (json.message || "โหลดข้อมูลไม่สำเร็จ"), "error");
      }
    } catch (e) {
      showToast("❌ ไม่สามารถเชื่อมต่อ API: " + e.message, "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  // ─── Search ──────────────────────────────────────────────────────────────────
  const handleSearch = useCallback(() => {
    fetchSummary(dateFrom, dateTo);
    fetchList(dateFrom, dateTo, statusFilter);
  }, [dateFrom, dateTo, statusFilter, fetchSummary, fetchList]);

  // ─── Auto Refresh ─────────────────────────────────────────────────────────────
  useEffect(() => {
    handleSearch();
    const timer = setInterval(handleSearch, AUTO_REFRESH_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Export Excel ─────────────────────────────────────────────────────────────
  const onExporting = useCallback((e) => {
    const fileName = `RPA_Log_${dateFrom}_${dateTo}.xlsx`;
    const workbook  = new Workbook();
    const worksheet = workbook.addWorksheet("RPA Log");
    exportDataGrid({ component: e.component, worksheet, autoFilterEnabled: true })
      .then(() => workbook.xlsx.writeBuffer())
      .then(buffer => saveAs(new Blob([buffer], { type: "application/octet-stream" }), fileName));
  }, [dateFrom, dateTo]);

  // ─── Row color (failed = light red) ──────────────────────────────────────────
  const onRowPrepared = useCallback((e) => {
    if (e.rowType !== "data") return;
    if (e.data?.status === "failed") {
      e.rowElement.style.backgroundColor = "#fff5f5";
    }
  }, []);

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="xl">
      <Toast toast={toast} />

      {/* Header row */}
      <Grid container spacing={2}><Grid item xs={12}>&nbsp;</Grid></Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={10}>
          <label className="head">RPA Tracking Monitor</label>
          {lastRefresh && (
            <span style={{ fontSize: 11, color: "#64748b", marginLeft: 12 }}>
              อัปเดตล่าสุด: {lastRefresh.toLocaleTimeString("th-TH")}
              {/* &nbsp;· auto-refresh ทุก 5 นาที */}
            </span>
          )}
        </Grid>
      </Grid>

      {/* Filter bar */}
      <Grid container spacing={2}>
        <Grid item xs={12} style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>

          <label style={{ fontSize: 13, color: "#475569", fontWeight: 600 }}>วันที่เริ่ม</label>
          <input
            type="date" value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13, color: "#1e293b" }}
          />

          <label style={{ fontSize: 13, color: "#475569", fontWeight: 600 }}>ถึง</label>
          <input
            type="date" value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13, color: "#1e293b" }}
          />

          <select
            value={statusFilter}
            onChange={e => {
              const val = e.target.value;
              setStatusFilter(val);
              fetchSummary(dateFrom, dateTo);
              fetchList(dateFrom, dateTo, val);
            }}
            style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13, color: "#1e293b", minWidth: 120 }}
          >
            <option value="">ทุก Status</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
          </select>

          <Button1
            className="searchButton" variant="contained" color="primary"
            onClick={handleSearch} disabled={loading}
          >
            {loading ? "⏳ กำลังโหลด..." : "Search"}
          </Button1>

          <Button1
            className="searchButton" variant="outlined" color="primary"
            onClick={() => { setDateFrom(today); setDateTo(today); setStatusFilter(""); }}
          >
            Clear Filter
          </Button1>

          <Button1
            className="searchButton" variant="outlined" color="primary"
            onClick={handleSearch} disabled={loading}
          >
            Refresh
          </Button1>

          <div style={{ width: 1, height: 28, background: "#e2e8f0", margin: "0 4px" }} />

          {/* Live status badge */}
          {summary && (
            <span style={{
              background: Number(summary.failed_count) > 0 ? "#fef2f2" : "#f0fdf4",
              color:      Number(summary.failed_count) > 0 ? "#dc2626" : "#16a34a",
              border:     `1px solid ${Number(summary.failed_count) > 0 ? "#fca5a5" : "#86efac"}`,
              borderRadius: 6, padding: "3px 12px", fontSize: 11, fontWeight: 800,
            }}>
              {Number(summary.failed_count) > 0
                ? `⚠️ มี ${summary.failed_count} รายการล้มเหลว`
                : "✅ ปกติทุกรายการ"}
            </span>
          )}
        </Grid>
      </Grid>

      {/* Summary Cards */}
      <SummaryCards summary={summary} />

      {/* DataGrid */}
      <Grid container className={classes.root}>
        <DataGrid
          ref={gridRef}
          dataSource={rows}
          keyExpr="id"
          showBorders={true}
          columnAutoWidth={false}
          rowAlternationEnabled={false}
          allowColumnReordering={true}
          allowColumnResizing={true}
          columnResizingMode="widget"
          columnMinWidth={50}
          height={height}
          hoverStateEnabled={true}
          repaintChangesOnly={true}
          wordWrapEnabled={false}
          renderAsync={true}
          cacheEnabled={true}
          onExporting={onExporting}
          onRowPrepared={onRowPrepared}
        >
          <Scrolling mode="virtual" rowRenderingMode="virtual" showScrollbar="always" useNative={false} scrollByThumb={true} />
          <Paging defaultPageSize={50} />
          <Pager allowedPageSizes={PAGE_SIZES} showPageSizeSelector={true} showInfo={true} />
          <LoadPanel enabled={loading} />
          <FilterRow visible={true} />
          <FilterPanel visible={true} />
          <HeaderFilter visible={true} />
          <SearchPanel visible={true} width={240} placeholder="Full Text Search..." />
          <ColumnChooser enabled={true} mode="select">
            <Position my="right top" at="right bottom" of=".dx-datagrid-column-chooser-button" />
            <ColumnChooserSearch enabled={true} editorOptions={SEARCH_OPTIONS} />
            <ColumnChooserSelection allowSelectAll={true} selectByClick={true} recursive={true} />
          </ColumnChooser>
          <StateStoring enabled={true} type="localStorage" storageKey="rpaMonitorGridState" />
          <Export enabled={true} allowExportSelectedData={false} />

          <Column
            dataField="received_at"
            caption="เวลา"
            width={170}
            dataType="string"
            fixed={true}
            fixedPosition="left"
            sortOrder="desc"
            sortIndex={0}
          />
          <Column dataField="booking_no"       caption="Booking No"   width={160} fixed={true} fixedPosition="left" />
          <Column dataField="carrier"          caption="Carrier"      width={160} />
          <Column dataField="container_count"  caption="Containers"   width={100} alignment="center" />
          <Column
            caption="Status"
            width={120}
            alignment="center"
            allowFiltering={false}
            allowSorting={true}
            dataField="status"
            cellRender={({ data }) => <StatusBadge data={data} />}
          />
          <Column
            dataField="processing_time_ms"
            caption="ms"
            width={80}
            alignment="right"
            dataType="number"
          />
          <Column
            dataField="error_message"
            caption="Error Message"
            minWidth={200}
            cellRender={({ data }) => <ErrorCell data={data} />}
          />
          <Column dataField="source_ip" caption="Source IP" width={130} />
        </DataGrid>
      </Grid>
    </Container>
  );
}
